import { GeckoComponentPath } from 'types/geometric/geckoComponentPath'
import { GeckoComponentSpace } from 'types/geometric/geckoComponentSpace'

import { GeckoMarkupPrimitive } from './geckoMarkupPrimitive'

/**
 * Markup polygons are similar to markup line primitives (in that they are
 * path-based lines on a component, connecting any number of points) but
 * polygons are slightly different in that they are closed shapes, with a line
 * connecting their first and last points and with their interior defined by
 * the even-odd rule. Note that points are in 2D component coordinates.
 * TODO ENT-1166: Describe how polygon wrapping works
 */
export class GeckoMarkupPolygon extends GeckoMarkupPrimitive {
  //----------------------------------------------------------------------------

  public readonly kind = 'polygon'
  public readonly path: GeckoComponentPath

  private constructor(
    id: string,
    markupLayerID: string,
    json: Record<string, unknown>,
    markupBounds: GeckoComponentSpace
  ) {
    if (!Array.isArray(json.points) || json.points.length === 0)
      throw new Error(`Primitive "${id}" malformed: ${JSON.stringify(json)}`)

    const points = json.points as { x: number | string; y: number | string }[]
    try {
      const path = GeckoComponentPath.CreateFromValues(
        markupBounds.componentID,
        points.concat(points[0]),
        markupBounds.x.unit,
        markupBounds.y.unit
      )
      super(id, markupLayerID, path.bounds)
      this.path = path
    } catch {
      throw new Error(`Primitive "${id}" malformed: ${JSON.stringify(json)}`)
    }
  }

  //----------------------------------------------------------------------------

  public static CreateFromJSON(
    id: string,
    markupLayerID: string,
    json: Record<string, unknown>,
    markupBounds: GeckoComponentSpace
  ) {
    return new GeckoMarkupPolygon(id, markupLayerID, json, markupBounds)
  }
}

/**
 * ---------------------------------------------------------------------------------------------------------------------
 *                                                    JSON-Type
 *           Copied from https://geckorobotics.atlassian.net/wiki/spaces/SE/pages/3958603801/Markup+Layer+Schema
 * ---------------------------------------------------------------------------------------------------------------------
 */

export type GeckoMarkupPolygonJSON = {
  kind: 'polygon'
  points: {
    x: number | 'Infinity' | '-Infinity'
    y: number | 'Infinity' | '-Infinity'
  }[] // 2+ values
}
